import React, { useState } from "react";
import { SCHEDULE, SITE, WHATSAPP_LINK } from "../lib/site-data";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Calendar, Clock, MessageCircle } from "lucide-react";

const LEVELS = [
  { id: "P3", label: "Primary 3" },
  { id: "P4", label: "Primary 4" },
  { id: "P5", label: "Primary 5" },
  { id: "P6", label: "Primary 6" },
];

const SUBJECTS = ["English", "Math"];

function ScheduleTable({ slots, level, subject }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-[#E2E8F0] bg-white">
      <table className="w-full text-left" data-testid={`schedule-table-${level}-${subject}`}>
        <thead className="bg-[#FAFAFA]">
          <tr>
            <th className="px-5 py-3 text-xs font-semibold text-[#475569] uppercase tracking-wider">
              Day
            </th>
            <th className="px-5 py-3 text-xs font-semibold text-[#475569] uppercase tracking-wider">
              Time
            </th>
            <th className="px-5 py-3 text-xs font-semibold text-[#475569] uppercase tracking-wider hidden sm:table-cell">
              Class
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#E2E8F0]">
          {slots.map((s, idx) => (
            <tr
              key={`${s.day}-${s.time}-${idx}`}
              className="hover:bg-[#FAFAFA]/60 transition-colors"
              data-testid={`schedule-row-${level}-${subject}-${idx}`}
            >
              <td className="px-5 py-4 text-sm font-medium text-[#0F172A]">
                <span className="inline-flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-[#1E3A8A]" />
                  {s.day}
                </span>
              </td>
              <td className="px-5 py-4 text-sm text-[#0F172A]">
                <span className="inline-flex items-center gap-2">
                  <Clock className="w-4 h-4 text-[#1E3A8A]" />
                  {s.time}
                </span>
              </td>
              <td className="px-5 py-4 text-sm text-[#475569] hidden sm:table-cell">
                {level} {subject}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Schedule() {
  const [subject, setSubject] = useState("English");

  return (
    <section
      id="schedule"
      className="py-20 lg:py-28 bg-white"
      data-testid="schedule-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
          <div className="max-w-2xl">
            <div className="text-sm font-medium text-[#D97706] uppercase tracking-widest mb-3">
              Weekly Schedule
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#0F172A] leading-tight">
              Find a slot that fits.
            </h2>
            <p className="mt-5 text-base text-[#475569] leading-relaxed">
              Pick a level and a subject below. Each class meets 4 times a
              month, with multiple weekly options for flexibility.
            </p>
          </div>

          <div
            className="inline-flex p-1 bg-[#FAFAFA] rounded-full border border-[#E2E8F0] self-start"
            data-testid="subject-toggle"
          >
            {SUBJECTS.map((s) => (
              <button
                key={s}
                onClick={() => setSubject(s)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                  subject === s
                    ? "bg-[#1E3A8A] text-white shadow-sm"
                    : "text-[#475569] hover:text-[#0F172A]"
                }`}
                data-testid={`subject-toggle-${s.toLowerCase()}`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        <Tabs defaultValue="P3" className="w-full">
          <TabsList
            className="h-auto flex-wrap gap-2 bg-transparent p-0 mb-6 justify-start"
            data-testid="level-tabs"
          >
            {LEVELS.map((lv) => (
              <TabsTrigger
                key={lv.id}
                value={lv.id}
                className="px-5 py-2.5 rounded-full data-[state=active]:bg-[#1E3A8A] data-[state=active]:text-white data-[state=active]:shadow-md bg-white border border-[#E2E8F0] text-[#0F172A] text-sm font-medium"
                data-testid={`level-tab-${lv.id}`}
              >
                {lv.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {LEVELS.map((lv) => (
            <TabsContent key={lv.id} value={lv.id} className="mt-0">
              <ScheduleTable
                slots={SCHEDULE[lv.id][subject]}
                level={lv.id}
                subject={subject}
              />
              <div className="mt-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 rounded-2xl bg-[#FAFAFA] border border-[#E2E8F0]">
                <p className="text-sm text-[#475569]">
                  Want to confirm a slot or check availability for{" "}
                  <span className="font-semibold text-[#0F172A]">
                    {lv.label} {subject}
                  </span>
                  ?
                </p>
                <a
                  href={WHATSAPP_LINK(
                    `Hi ${SITE.name}, I'd like to check availability for ${lv.label} ${subject} classes.`
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-[#1E3A8A] text-white hover:bg-[#1E40AF] px-5 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap"
                  data-testid={`schedule-cta-${lv.id}`}
                >
                  <MessageCircle className="w-4 h-4" />
                  Ask on WhatsApp
                </a>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </section>
  );
}
